import { useEffect, useState } from "react";
import { findSpotlightTargetElement } from "./spotlightTargets";

export const TARGET_STATUS = {
	NONE: "none",
	SEARCHING: "searching",
	FOUND: "found",
	MISSING: "missing",
	GONE: "gone",
};

const DEFAULT_MISSING_AFTER_MS = 1800;
const GONE_AFTER_MS = 300;
const SCROLL_SAFE_TOP = 96;
const SCROLL_SAFE_BOTTOM = 24;

const toRect = ({ left, top, right, bottom }) => ({
	left: Math.round(left),
	top: Math.round(top),
	right: Math.round(right),
	bottom: Math.round(bottom),
});

const intersectsViewport = (rect) =>
	rect.right > 0 && rect.bottom > 0 && rect.left < window.innerWidth && rect.top < window.innerHeight;

/**
 * Bounds of the element plus any visible descendants. Interactive steps use this so popovers that
 * overflow the target (open dropdown lists) stay inside the clickable hole.
 */
const getInteractiveBounds = (element) => {
	const bounds = element.getBoundingClientRect();
	let { left, top, right, bottom } = bounds;
	element.querySelectorAll("*").forEach((child) => {
		const rect = child.getBoundingClientRect();
		if (rect.width === 0 || rect.height === 0 || !intersectsViewport(rect)) return;
		left = Math.min(left, rect.left);
		top = Math.min(top, rect.top);
		right = Math.max(right, rect.right);
		bottom = Math.max(bottom, rect.bottom);
	});
	return { left, top, right, bottom };
};

const isScrollContainer = (node) => {
	const { overflowY } = getComputedStyle(node);
	return /(auto|scroll)/.test(overflowY) && node.scrollHeight > node.clientHeight;
};

/** Scrolling can only help if a scroll container sits between the element and the nearest fixed layer (fixed headers and modals never move). */
const canScrollToElement = (element) => {
	for (let node = element; node && node !== document.body; node = node.parentElement) {
		if (isScrollContainer(node)) return true;
		if (getComputedStyle(node).position === "fixed") return false;
	}
	return true;
};

const scrollIntoViewIfNeeded = (element, rect) => {
	if (!canScrollToElement(element)) return;
	const isComfortablyVisible = rect.top >= SCROLL_SAFE_TOP && rect.bottom <= window.innerHeight - SCROLL_SAFE_BOTTOM;
	if (isComfortablyVisible) return;
	const isTall = rect.bottom - rect.top > window.innerHeight * 0.6;
	element.scrollIntoView({ behavior: "smooth", block: isTall ? "start" : "center", inline: "nearest" });
};

const isSameState = (a, b) =>
	a.status === b.status &&
	(a.rect === b.rect || (a.rect && b.rect && a.rect.left === b.rect.left && a.rect.top === b.rect.top && a.rect.right === b.rect.right && a.rect.bottom === b.rect.bottom));

/**
 * Tracks the on-screen rectangle of a spotlight target every animation frame, so the hole follows
 * scrolling, resizing, animations and layout shifts. Scrolls the target into view when a step starts.
 * `resetKey` restarts the search (use the step id). `missingAfterMs` is how long to wait for a slow-loading target.
 */
const useSpotlightTargetRect = ({ targetId, interactive, enabled, resetKey, missingAfterMs = DEFAULT_MISSING_AFTER_MS }) => {
	const [state, setState] = useState({ status: targetId ? TARGET_STATUS.SEARCHING : TARGET_STATUS.NONE, rect: null });

	useEffect(() => {
		if (!enabled) return undefined;
		if (!targetId) {
			setState({ status: TARGET_STATUS.NONE, rect: null });
			return undefined;
		}

		setState({ status: TARGET_STATUS.SEARCHING, rect: null });
		const startedAt = performance.now();
		let hasScrolled = false;
		let hasBeenFound = false;
		let missingSince = null;
		let latest = { status: TARGET_STATUS.SEARCHING, rect: null };
		let frame;

		const publish = (next) => {
			if (isSameState(latest, next)) return;
			latest = next;
			setState(next);
		};

		const tick = () => {
			const element = findSpotlightTargetElement(targetId);
			const now = performance.now();

			if (element) {
				hasBeenFound = true;
				missingSince = null;
				const rect = toRect(interactive ? getInteractiveBounds(element) : element.getBoundingClientRect());
				if (!hasScrolled) {
					hasScrolled = true;
					scrollIntoViewIfNeeded(element, rect);
				}
				publish({ status: TARGET_STATUS.FOUND, rect });
			} else if (hasBeenFound) {
				missingSince = missingSince ?? now;
				if (now - missingSince > GONE_AFTER_MS) publish({ status: TARGET_STATUS.GONE, rect: null });
			} else if (now - startedAt > missingAfterMs) {
				publish({ status: TARGET_STATUS.MISSING, rect: null });
			}

			frame = requestAnimationFrame(tick);
		};

		frame = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(frame);
	}, [targetId, interactive, enabled, resetKey, missingAfterMs]);

	return state;
};

export default useSpotlightTargetRect;
